import { useCallback, useMemo, useState } from 'react';

type UseSelectedItemsProps = {
  keys: string[];
  resetOn?: unknown[];
};

type UseSelectedItemsReturn = {
  selectedKeys: string[];
  isSelected: (key: string) => boolean;
  isAllSelected: boolean;
  setSelected: (key: string, isSelected: boolean) => void;
  toggleSelected: (key: string) => void;
  setAllSelected: (isSelected: boolean) => void;
  clearSelection: () => void;
};

export const useSelectedItems = ({
  keys,
  resetOn = [],
}: UseSelectedItemsProps): UseSelectedItemsReturn => {
  const [selection, setSelection] = useState<Set<string>>(new Set());

  const resetKey = JSON.stringify(resetOn);
  const [appliedResetKey, setAppliedResetKey] = useState(resetKey);
  if (resetKey !== appliedResetKey) {
    setAppliedResetKey(resetKey);
    setSelection(new Set());
  }

  const selectedKeys = useMemo(() => keys.filter((key) => selection.has(key)), [keys, selection]);

  const isSelected = useCallback((key: string): boolean => selection.has(key), [selection]);

  const setSelected = useCallback((key: string, isChecked: boolean): void => {
    setSelection((current) => {
      const next = new Set(current);
      if (isChecked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  }, []);

  const toggleSelected = useCallback((key: string): void => {
    setSelection((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const setAllSelected = useCallback(
    (isChecked: boolean): void => setSelection(isChecked ? new Set(keys) : new Set()),
    [keys],
  );

  const clearSelection = useCallback((): void => setSelection(new Set()), []);

  return {
    selectedKeys,
    isSelected,
    isAllSelected: keys.length > 0 && selectedKeys.length === keys.length,
    setSelected,
    toggleSelected,
    setAllSelected,
    clearSelection,
  };
};
